import type { TransactionDetails } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import type { ConnectedWallet } from '@/hooks/wallets/useOnboard'
import { isMultisigExecutionInfo } from '@/utils/transaction-guards'
import { isEthSignWallet, isSmartContractWallet } from '@/utils/wallets'
import type { MultiSendCallOnlyContractImplementationType } from '@safe-global/protocol-kit'
import { cgwApi as relayApi } from '@safe-global/store/gateway/AUTO_GENERATED/relay'
import { getStoreInstance } from '@/store'
import { type Chain } from '@safe-global/store/gateway/AUTO_GENERATED/chains'
import { type SafeState } from '@safe-global/store/gateway/AUTO_GENERATED/safes'

import type {
  SafeSignature,
  SafeTransaction,
  Transaction,
  TransactionOptions,
  TransactionResult,
} from '@safe-global/types-kit'
import { didRevert } from '@/utils/ethers-utils'
import type { Eip1193Provider, Overrides, TransactionResponse } from 'ethers'
import type { RequestId } from '@safe-global/safe-apps-sdk'
import proposeTx from '../proposeTransaction'
import { txDispatch, TxEvent } from '../txEvents'
import { waitForRelayedTx } from '@/services/tx/txMonitor'
import { getReadOnlyCurrentGnosisSafeContract } from '@/services/contracts/safeContracts'
import {
  getAndValidateSafeSDK,
  getSafeSDKWithSigner,
  tryOffChainTxSigning,
  getUncheckedSigner,
  prepareTxExecution,
  prepareApproveTxHash,
} from './sdk'
import { createWeb3, getUserNonce } from '@/hooks/wallets/web3'
import { asError } from '@safe-global/utils/services/exceptions/utils'
import chains from '@safe-global/utils/config/chains'
import { createExistingTx } from './create'
import { getRelaySimulationError } from '@safe-global/utils/services/relayErrors'

import { getLatestSafeVersion } from '@safe-global/utils/utils/chains'

/**
 * Propose a transaction
 * If txId is passed, it's an existing tx being signed
 */
export const dispatchTxProposal = async ({
  chainId,
  safeAddress,
  sender,
  safeTx,
  txId,
  origin,
}: {
  chainId: string
  safeAddress: string
  sender: string
  safeTx: SafeTransaction
  txId?: string
  origin?: string
}): Promise<TransactionDetails> => {
  const safeSDK = getAndValidateSafeSDK()
  const safeTxHash = await safeSDK.getTransactionHash(safeTx)

  let proposedTx: TransactionDetails | undefined
  try {
    proposedTx = await proposeTx(chainId, safeAddress, sender, safeTx, safeTxHash, origin)
  } catch (error) {
    if (txId) {
      txDispatch(TxEvent.SIGNATURE_PROPOSE_FAILED, { txId, chainId, safeAddress, error: asError(error) })
    } else {
      txDispatch(TxEvent.PROPOSE_FAILED, { error: asError(error) })
    }
    throw error
  }

  // Dispatch a success event only if the tx is signed
  // Unsigned txs are proposed only temporarily and won't appear in the queue
  if (safeTx.signatures.size > 0) {
    txDispatch(txId ? TxEvent.SIGNATURE_PROPOSED : TxEvent.PROPOSED, {
      txId: proposedTx?.txId,
      signerAddress: txId ? sender : undefined,
      nonce: safeTx.data.nonce,
      chainId,
      safeAddress,
    })
  }

  return proposedTx
}

/**
 * Sign a transaction
 */
export const dispatchTxSigning = async (
  safeTx: SafeTransaction,
  provider: Eip1193Provider,
  txId?: string,
): Promise<SafeTransaction> => {
  const sdk = await getSafeSDKWithSigner(provider)

  let signedTx: SafeTransaction | undefined
  try {
    signedTx = await tryOffChainTxSigning(safeTx, sdk)
  } catch (error) {
    txDispatch(TxEvent.SIGN_FAILED, {
      txId,
      error: asError(error),
    })
    throw error
  }

  txDispatch(TxEvent.SIGNED, { txId })

  return signedTx
}

// We have to manually sign because sdk.signTransaction doesn't support proposers
export const dispatchProposerTxSigning = async (safeTx: SafeTransaction, wallet: ConnectedWallet) => {
  const sdk = await getSafeSDKWithSigner(wallet.provider)

  let signature: SafeSignature
  if (isEthSignWallet(wallet)) {
    const txHash = await sdk.getTransactionHash(safeTx)
    signature = await sdk.signHash(txHash)
  } else {
    signature = await sdk.signTypedData(safeTx)
  }

  safeTx.addSignature(signature)

  return safeTx
}

const ZK_SYNC_ON_CHAIN_SIGNATURE_GAS_LIMIT = 4_500_000

/**
 * On-Chain sign a transaction
 */
export const dispatchOnChainSigning = async (
  safeTx: SafeTransaction,
  txId: string,
  provider: Eip1193Provider,
  chainId: SafeState['chainId'],
  signerAddress: string,
  safeAddress: string,
  isNestedSafe: boolean,
) => {
  const sdk = await getSafeSDKWithSigner(provider)
  const safeTxHash = await sdk.getTransactionHash(safeTx)
  const eventParams = { txId, nonce: safeTx.data.nonce, chainId, safeAddress }

  const options =
    chainId === chains.zksync || chainId === chains.lens
      ? { gasLimit: ZK_SYNC_ON_CHAIN_SIGNATURE_GAS_LIMIT }
      : undefined
  let txHashOrParentSafeTxHash: string
  try {
    // TODO: This is a workaround until there is a fix for unchecked transactions in the protocol-kit
    const encodedApproveHashTx = await prepareApproveTxHash(safeTxHash, provider)

    // Note: SafeWalletProvider returns transaction hash if it exists, otherwise the safeTxHash
    // If the parent immediately executes, this will be the transaction hash of the approveHash
    // otherwise the safeTxHash of it
    txHashOrParentSafeTxHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [{ from: signerAddress, to: safeAddress, data: encodedApproveHashTx, gas: options?.gasLimit }],
    })

    txDispatch(TxEvent.ONCHAIN_SIGNATURE_REQUESTED, eventParams)
  } catch (err) {
    txDispatch(TxEvent.FAILED, { ...eventParams, error: asError(err) })
    throw err
  }

  txDispatch(TxEvent.ONCHAIN_SIGNATURE_SUCCESS, eventParams)

  if (isNestedSafe) {
    txDispatch(TxEvent.NESTED_SAFE_TX_CREATED, {
      ...eventParams,
      txHashOrParentSafeTxHash,
      parentSafeAddress: signerAddress,
    })
  }

  // Until the on-chain signature is/has been executed, the safeTx is not
  // signed so we don't return it
}

export const dispatchSafeTxSpeedUp = async (
  txOptions: Omit<TransactionOptions, 'nonce'> & { nonce: number },
  txId: string,
  provider: Eip1193Provider,
  chainId: SafeState['chainId'],
  signerAddress: string,
  safeAddress: string,
  nonce: number,
) => {
  const sdk = await getSafeSDKWithSigner(provider)
  const eventParams = { txId, nonce, chainId, safeAddress }
  const signerNonce = txOptions.nonce
  const isSmartAccount = await isSmartContractWallet(chainId, signerAddress)

  // Execute the tx
  let result: TransactionResult | undefined
  try {
    const safeTx = await createExistingTx(chainId, txId)

    // TODO: This is a workaround until there is a fix for unchecked transactions in the protocol-kit
    if (isSmartAccount) {
      const encodedTx = await prepareTxExecution(safeTx, provider)
      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{ from: signerAddress, to: safeAddress, data: encodedTx }],
      })

      result = {
        hash: txHash,
        transactionResponse: null,
      }
    } else {
      result = await sdk.executeTransaction(safeTx, txOptions)
    }
    txDispatch(TxEvent.EXECUTING, eventParams)
  } catch (error) {
    txDispatch(TxEvent.SPEEDUP_FAILED, { ...eventParams, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING, {
    ...eventParams,
    txHash: result.hash,
    signerAddress,
    signerNonce,
    gasLimit: txOptions.gasLimit?.toString(),
    txType: 'SafeTx',
  })

  return result.hash
}

export const dispatchCustomTxSpeedUp = async (
  txOptions: Omit<TransactionOptions, 'nonce'> & { nonce: number },
  txId: string,
  to: string,
  data: string,
  provider: Eip1193Provider,
  chainId: string,
  signerAddress: string,
  safeAddress: string,
  nonce: number,
) => {
  const eventParams = { txId, nonce, chainId, safeAddress }
  const signerNonce = txOptions.nonce

  // Execute the tx
  let result: TransactionResponse | undefined
  try {
    const signer = await getUncheckedSigner(provider)
    result = await signer.sendTransaction({ to, data, ...txOptions })
    txDispatch(TxEvent.EXECUTING, eventParams)
  } catch (error) {
    txDispatch(TxEvent.SPEEDUP_FAILED, { ...eventParams, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING, {
    txHash: result.hash,
    signerAddress,
    signerNonce,
    data,
    to,
    groupKey: result?.hash,
    txType: 'Custom',
    nonce,
    chainId,
    safeAddress,
  })

  return result.hash
}

/**
 * Execute a transaction
 */
export const dispatchTxExecution = async (
  chainId: string,
  safeTx: SafeTransaction,
  txOptions: TransactionOptions,
  txId: string,
  provider: Eip1193Provider,
  signerAddress: string,
  safeAddress: string,
  isSmartAccount: boolean,
): Promise<string> => {
  const sdk = await getSafeSDKWithSigner(provider)
  const eventParams = { txId, nonce: safeTx.data.nonce, chainId, safeAddress }

  const signerNonce = txOptions.nonce ?? (await getUserNonce(signerAddress))

  // Execute the tx
  let result: TransactionResult | undefined
  try {
    // TODO: This is a workaround until there is a fix for unchecked transactions in the protocol-kit
    if (isSmartAccount) {
      const encodedTx = await prepareTxExecution(safeTx, provider)
      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{ from: signerAddress, to: safeAddress, data: encodedTx }],
      })

      result = {
        hash: txHash,
        transactionResponse: null,
      }
    } else {
      result = await sdk.executeTransaction(safeTx, txOptions)
    }
    txDispatch(TxEvent.EXECUTING, { ...eventParams })
  } catch (error) {
    txDispatch(TxEvent.FAILED, { ...eventParams, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING, {
    ...eventParams,
    nonce: safeTx.data.nonce,
    txHash: result.hash,
    signerAddress,
    signerNonce,
    gasLimit: txOptions.gasLimit?.toString(),
    txType: 'SafeTx',
  })

  return result.hash
}

export const dispatchBatchExecution = async (
  txs: TransactionDetails[],
  multiSendContract: MultiSendCallOnlyContractImplementationType,
  multiSendTxData: `0x${string}`,
  provider: Eip1193Provider,
  chainId: string,
  signerAddress: string,
  safeAddress: string,
  overrides: Omit<Overrides, 'nonce'> & { nonce: number },
  nonce: number,
) => {
  const groupKey = multiSendTxData

  let result: TransactionResponse
  const txIds = txs.map((tx) => tx.txId)
  let signerNonce = overrides.nonce
  let txData = multiSendContract.encode('multiSend', [multiSendTxData])

  try {
    if (signerNonce === undefined || signerNonce === null) {
      signerNonce = await getUserNonce(signerAddress)
    }
    const signer = await getUncheckedSigner(provider)

    result = await signer.sendTransaction({
      to: multiSendContract.getAddress(),
      value: '0',
      data: txData,
      ...overrides,
    })

    txIds.forEach((txId) => {
      txDispatch(TxEvent.EXECUTING, { txId, groupKey, nonce, chainId, safeAddress })
    })
  } catch (err) {
    txIds.forEach((txId) => {
      txDispatch(TxEvent.FAILED, { txId, error: asError(err), groupKey, nonce, chainId, safeAddress })
    })
    throw err
  }
  const txTo = multiSendContract.getAddress()

  txIds.forEach((txId) => {
    txDispatch(TxEvent.PROCESSING, {
      txId,
      txHash: result.hash,
      groupKey,
      signerNonce,
      signerAddress,
      txType: 'Custom',
      data: txData,
      to: txTo,
      nonce,
      chainId,
      safeAddress,
    })
  })

  return result!.hash
}

/**
 * Execute a module transaction
 */
export const dispatchModuleTxExecution = async (
  tx: Transaction,
  provider: Eip1193Provider,
  chainId: string,
  safeAddress: string,
): Promise<string> => {
  const id = JSON.stringify(tx)

  let result: TransactionResponse | undefined
  try {
    const browserProvider = createWeb3(provider)
    const signer = await browserProvider.getSigner()

    txDispatch(TxEvent.EXECUTING, { groupKey: id, chainId, safeAddress })
    result = await signer.sendTransaction(tx)
  } catch (error) {
    txDispatch(TxEvent.FAILED, { groupKey: id, chainId, safeAddress, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING_MODULE, {
    groupKey: id,
    txHash: result.hash,
  })

  result
    ?.wait()
    .then((receipt) => {
      if (receipt === null) {
        txDispatch(TxEvent.FAILED, {
          groupKey: id,
          chainId,
          safeAddress,
          error: new Error('No transaction receipt found'),
        })
      } else if (didRevert(receipt)) {
        txDispatch(TxEvent.REVERTED, {
          groupKey: id,
          chainId,
          safeAddress,
          error: new Error('Transaction reverted by EVM'),
        })
      } else {
        txDispatch(TxEvent.PROCESSED, { groupKey: id, chainId, safeAddress, txHash: result?.hash })
      }
    })
    .catch((error) => {
      txDispatch(TxEvent.FAILED, { groupKey: id, chainId, safeAddress, error: asError(error) })
    })

  return result?.hash
}

export async function dispatchSafeAppsTx(
  args: { safeAppRequestId: RequestId; txId?: string } & (
    | { safeTx: SafeTransaction; provider: Eip1193Provider }
    | { safeTxHash: string }
  ),
): Promise<string> {
  let safeTxHash: string
  if ('safeTx' in args && 'provider' in args) {
    const { safeTx, provider } = args
    const sdk = await getSafeSDKWithSigner(provider)
    safeTxHash = await sdk.getTransactionHash(safeTx)
  } else {
    safeTxHash = args.safeTxHash
  }

  const { txId, safeAppRequestId } = args
  txDispatch(TxEvent.SAFE_APPS_REQUEST, { safeAppRequestId, safeTxHash, txId })
  return safeTxHash
}

export const dispatchTxRelay = async (
  safeTx: SafeTransaction,
  safe: SafeState,
  txId: string,
  chain: Chain,
  gasLimit?: string | number | bigint,
  acceptUnverifiedSimulation?: boolean,
) => {
  const store = getStoreInstance()
  const readOnlySafeContract = await getReadOnlyCurrentGnosisSafeContract(safe)
  const safeSDK = getAndValidateSafeSDK()
  const safeTxHash = await safeSDK.getTransactionHash(safeTx)

  let transactionToRelay = safeTx
  const data = readOnlySafeContract.encode('execTransaction', [
    transactionToRelay.data.to,
    transactionToRelay.data.value,
    transactionToRelay.data.data,
    transactionToRelay.data.operation,
    transactionToRelay.data.safeTxGas,
    transactionToRelay.data.baseGas,
    transactionToRelay.data.gasPrice,
    transactionToRelay.data.gasToken,
    transactionToRelay.data.refundReceiver,
    transactionToRelay.encodedSignatures(),
  ])

  try {
    const relayAction = relayApi.endpoints.relayRelayV1.initiate({
      chainId: safe.chainId,
      relayDto: {
        to: safe.address.value,
        data,
        gasLimit: gasLimit?.toString(),
        version: safe.version ?? getLatestSafeVersion(chain),
        safeTxHash,
        acceptUnverifiedSimulation,
      },
    })

    const relayResponse = await store.dispatch(relayAction).unwrap()
    const taskId = relayResponse.taskId

    if (!taskId) {
      throw new Error('Transaction could not be relayed')
    }

    txDispatch(TxEvent.RELAYING, {
      taskId,
      txId,
      nonce: safeTx.data.nonce,
      chainId: safe.chainId,
      safeAddress: safe.address.value,
    })

    // Monitor relay tx
    waitForRelayedTx(taskId, [txId], safe.chainId, safe.address.value, safeTx.data.nonce)
  } catch (error) {
    // CGW pre-relay simulation surfaces SIMULATION_FAILED / INDETERMINATE_SIMULATION as a typed
    // error so the UI can block or offer an explicit retry; everything else stays as-is.
    const finalError = getRelaySimulationError(error) ?? asError(error)
    txDispatch(TxEvent.FAILED, {
      txId,
      error: finalError,
      nonce: safeTx.data.nonce,
      chainId: safe.chainId,
      safeAddress: safe.address.value,
    })
    throw finalError
  }
}

export const dispatchBatchExecutionRelay = async (
  txs: TransactionDetails[],
  multiSendContract: MultiSendCallOnlyContractImplementationType,
  multiSendTxData: `0x${string}`,
  chainId: string,
  safeAddress: string,
  safeVersion: string,
) => {
  const store = getStoreInstance()
  const to = multiSendContract.getAddress()

  const data = multiSendContract.encode('multiSend', [multiSendTxData])
  const groupKey = multiSendTxData

  let relayResponse
  try {
    const relayAction = relayApi.endpoints.relayRelayV1.initiate({
      chainId,
      relayDto: {
        to,
        data,
        version: safeVersion,
      },
    })

    relayResponse = await store.dispatch(relayAction).unwrap()
  } catch (error) {
    txs.forEach(({ txId }) => {
      txDispatch(TxEvent.FAILED, {
        txId,
        chainId,
        safeAddress,
        error: asError(error),
        groupKey,
      })
    })
    throw error
  }

  const taskId = relayResponse.taskId
  txs.forEach(({ txId, detailedExecutionInfo }) => {
    if (isMultisigExecutionInfo(detailedExecutionInfo)) {
      txDispatch(TxEvent.RELAYING, { taskId, txId, groupKey, nonce: detailedExecutionInfo.nonce, chainId, safeAddress })
    }
  })

  // Monitor relay tx
  waitForRelayedTx(
    taskId,
    txs.map((tx) => tx.txId),
    chainId,
    safeAddress,
    isMultisigExecutionInfo(txs[0].detailedExecutionInfo) ? txs[0].detailedExecutionInfo.nonce : 0,
    groupKey,
  )
}
